import Business from '@mui/icons-material/Business';
import { Box, Grid, List, Paper } from '@mui/material';
import AlphabetSortDrawer from 'components/ui/AlphabetSortDrawer';
import CompanyView from 'components/ui/CompanyView';
import { PageHeader } from 'components/ui/Global/PageHeader';
import { SearchFilter } from 'components/ui/SearchFilter';
import { SingleCompanyView } from 'components/ui/SingleCompanyView';
import { companyTypes } from 'data/enum';
import { emptyCompany } from 'data/templates';
import { getFilteredList } from 'functions/misc';
import useWindowsDimension from 'functions/useWindowsDimension';
import { rq } from 'http/apiRoutes';
import { useGet, usePost, usePut, useDel } from 'http/useApi';
import { ICompanyViewModel, ThirdPartEnum } from 'Interfaces/ICompaniesConfig';
import { ICompanyProductViewModel } from 'Interfaces/ICompanyProductConfig';
import {
  EnumCompanyType,
  IAddressViewModel,
} from 'Interfaces/IGlobalTypeConfig';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const initialCompanyState: ICompanyViewModel = {
  id: 'new',
  organizationNumber: '',
  alias: '',
  name: '',
  mainAddress: {
    street: '',
    area: '',
    postalCode: '',
    city: '',
    country: 'Norway',
  } as IAddressViewModel,
  deliveryAddress: {
    street: '',
    area: '',
    postalCode: '',
    city: '',
    country: 'Norway',
  } as IAddressViewModel,
  invoiceAddress: {
    street: '',
    area: '',
    postalCode: '',
    city: '',
    country: 'Norway',
  } as IAddressViewModel,
  phone: '+47',
  email: '',
  companyType: 2,
  webPage: '',
  url: '',
  productBilling: true,
  onHold: false,
  isSupport: false,
  onHoldNote: '',
  priority: 2,
  fixedHourlyRate: 0,
  workRole: null,
  contactId: null,
  contact: null,
  companyProducts: [] as ICompanyProductViewModel[],
  legacyId: 0,
  thirdPartyId: '',
  thirdParty: ThirdPartEnum.None,
  creatorId: null,
};

export type CompaniesListProps = {
  handleChange?: (
    event: React.MouseEvent<HTMLElement>,
    company: ICompanyViewModel
  ) => void;
  isHideCompanyView?: boolean;
  isHideCompanyNavigation?: boolean;
  companyType?: EnumCompanyType; // Default to 2 (Kunder)
};

export const CompaniesList = ({
  handleChange,
  isHideCompanyView,
  isHideCompanyNavigation,
  companyType = EnumCompanyType.CLIENT,
}: CompaniesListProps) => {
  const { t } = useTranslation();
  let navigate = useNavigate();
  const { height } = useWindowsDimension();
  const ref = useRef<EnumCompanyType>(companyType);
  // API
  const { data } = useGet(rq.companies(ref.current.toFixed(0), true));

  console.log('is  HideCompanyView', isHideCompanyView);
  console.log('is  HideCompanyNavigation', isHideCompanyNavigation);

  // State
  const [company, setCompany] =
    useState<ICompanyViewModel>(initialCompanyState);
  const [companies, setCompanies] = useState<ICompanyViewModel[]>([]);
  const [filterType, setFilterType] = useState<number>(
    companyType || EnumCompanyType.CLIENT
  );
  const [letterFilter, setLetterFilter] = useState('');

  // Handlers
  const handleNew = () => {
    navigate('/companies/new');
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!data?.companies) return;
    const filtered = getFilteredList(data.companies, e.target.value);
    setCompanies(filtered);
  };

  const handleFilterChange = (val: number) => {
    setFilterType(val as EnumCompanyType);
    ref.current = val as EnumCompanyType;
  };

  const filteredCompanies = companies.filter((cmp) =>
    letterFilter === '' ? true : cmp.name?.[0]?.toUpperCase() === letterFilter
  );

  // On data load/update, clone to local list
  useEffect(() => {
    if (!data?.companies) return;
    console.log('data.companies', data.companies);
    setCompanies(_.cloneDeep(data.companies));
  }, [data]);

  // Whenever 'company' changes, call the parent's 'handleChange'

  return (
    <Grid container sx={{ display: 'flex', flexDirection: 'row', flexGrow: 1 }}>
      <Grid item xs={12}>
        <PageHeader
          Icon={Business}
          title={t('company.header')}
          FormJSX={null}
          HeaderButtons={[]}
        />
      </Grid>

      <Grid item xs={isHideCompanyView ? 12 : 5} sx={{ pr: 1 }}>
        <SearchFilter
          Icon={Business}
          Header={t('company.header')}
          filterValue={filterType}
          handleFilterChange={handleFilterChange}
          handleSearch={handleSearch}
          handleNew={handleNew}
          selectList={companyTypes}
        />
        <Box sx={{ display: 'flex' }}>
          <AlphabetSortDrawer
            buttonIcon={<></>}
            buttonContext='companies'
            handleChange={() => {}}
            onLetterChange={(letter: string) => setLetterFilter(letter)}
          />
          <List
            sx={{
              height: Math.ceil(height - 244),
              width: '100%',
              mb: 0,
              p: 2,
              backgroundColor: 'primary.back',
              overflow: 'auto',
            }}
          >
            {filteredCompanies.length === 0 && <div>No data</div>}
            {filteredCompanies.map((cmp, index) => (
              <Grid
                key={index}
                sx={{
                  cursor: 'pointer',
                  marginBottom: 1,
                  backgroundColor: 'red',
                  borderRadius: 2,
                }}
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  e.preventDefault();
                  setCompany(cmp);
                  handleChange?.(e, cmp);
                }}
              >
                <SingleCompanyView
                  key={index}
                  index={index}
                  company={cmp}
                  detailNav={isHideCompanyNavigation}
                />
              </Grid>
            ))}
          </List>
        </Box>

        <Paper
          variant='elevation3'
          sx={{
            p: 2,
            height: 64,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        />
      </Grid>

      <Grid item xs={isHideCompanyView ? 0 : 7} sx={{ pl: 1 }}>
        {!isHideCompanyView && (
          <Paper variant='elevation3'>
            <List
              sx={{
                pl: 2,
                pr: 2,
                height: Math.ceil(height - 128),
                backgroundColor: 'primary.main',
                overflow: 'auto',
                borderRadius: 3,
              }}
            >
              {company.id ? (
                <CompanyView company={company} />
              ) : (
                <CompanyView company={emptyCompany} />
              )}
            </List>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};
